import { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import Button from '@/components/Button/Button'
import UIIcon, { isIcon, UIIconRef, UIIconType } from '@/components/UI/UIIcon'
import UITag from '@/components/UI/UITag'
import { useAppState } from '@/composables/useAppState'
import { useGSAP } from '@/composables/useGSAP'
import { useImageFormatContext } from '@/composables/useImageFormatContext'
import useTracking from '@/composables/useTracking'
import { useTransComponents } from '@/composables/useTransComponents'
import { UIAudio } from '@/config/AudioConfig'
import { getContentfulUrl } from '@/utils/contentful'
import AudioEngine from '@/webgl/audio/AudioEngine'

import UIParticles from '../Particles/UIParticles'

const backgroundColors = ['text-mid-blue', 'text-light-blue', 'text-coral', 'text-cream']
const textColors = ['text-dark-cream', 'text-primary', 'text-primary', 'text-primary']
const excludedColors = [['light-blue'], ['light-blue'], ['coral'], ['cream', 'dark-cream', 'coral', 'scarlet', 'peach']]

// NO MORE ACCENTS, THOSE COLORS ARE THE SAME AS THE TEXT COLORS
const accentColors = ['var(--color-dark-cream)', 'var(--color-primary)', 'var(--color-primary)', 'var(--color-primary)']

type PopinCardStepProps = React.HTMLProps<HTMLDivElement> & {
  id: string
  baseI18nKey: string
  step: number
  isLast: boolean
  ctaI18nKey: string
  hideCta?: boolean
  noSticker?: boolean

  onNext: () => void
}

const PopinCardStep = forwardRef<HTMLDivElement, PopinCardStepProps>(
  ({ className, id, baseI18nKey, step, isLast, ctaI18nKey, onNext, hideCta = false }: PopinCardStepProps, ref) => {
    const { t } = useTranslation()
    const { components } = useTransComponents()

    const { gsap } = useGSAP()

    const translatedBackground = t(`${baseI18nKey}.background`)
    const innerBackground: UIIconType = (
      isIcon(translatedBackground) ? translatedBackground : 'background1'
    ) as UIIconType

    const translatedSticker = t(`${baseI18nKey}.sticker`)

    const innerAccent = parseInt(t(`${baseI18nKey}.color`))

    const backgroundIconRef = useRef<UIIconRef>(null)
    const contentRef = useRef<HTMLDivElement>(null)

    const betterFormat = useImageFormatContext()
    const [stickerUrl, setImageUrl] = useState(null)
    useEffect(() => {
      const url = getContentfulUrl(`/src/assets/runner/${translatedSticker}.png`, {
        w: 608,
        fm: betterFormat,
      })
      setImageUrl(url)
    }, [translatedSticker, betterFormat])

    useLayoutEffect(() => {
      const ctx = gsap.context(() => {
        AudioEngine.play(UIAudio.Pop)
        const timeline = gsap.timeline()

        timeline.addLabel('start')
        timeline.fromTo(
          backgroundIconRef.current,
          {
            opacity: 0,
          },
          {
            opacity: 1,
            duration: 0.5,
            ease: 'power3.out',
          },
          'start'
        )
        timeline.fromTo(
          backgroundIconRef.current,
          {
            scale: 0,
            rotate: step % 2 === 0 ? 90 : -90,
          },
          {
            scale: 1,
            rotate: 0,
            clearProps: 'scale,rotate',
            duration: 0.5,
            ease: 'back.out(1.7)',
          },
          'start'
        )
        timeline.fromTo(
          contentRef.current,
          {
            opacity: 0,
            // y: 64,
          },
          {
            opacity: 1,
            // y: 0,
            duration: 0.5,
            ease: 'power3.out',
          },
          'start+=0.4'
        )

        const sticker = contentRef.current.querySelector('.Sticker')
        if (sticker) {
          timeline.fromTo(
            sticker,
            {
              yPercent: -50,
              rotate: step % 2 === 0 ? -25 : 25,
              transformOrigin: '50% 100%',
            },
            {
              yPercent: 0,
              rotate: 0,
              duration: 0.5,
              ease: 'power4.out',
            },
            'start+=0.4'
          )
        }

        const last = contentRef.current.querySelector('.Last')
        if (last) {
          timeline.fromTo(
            last,
            {
              y: 64,
            },
            {
              y: 0,
              duration: 0.5,
              ease: 'power4.out',
            },
            'start+=0.7'
          )
          timeline.fromTo(
            last,
            {
              opacity: 0,
            },
            {
              opacity: 1,
              duration: 0.5,
              ease: 'power3.out',
            },
            'start+=0.7'
          )
        }
      })

      return () => ctx.revert()
    }, [step, gsap])

    const { trackImpression, trackClick } = useTracking()
    const { persona, scenarioUnitName } = useAppState()
    useLayoutEffect(() => {
      const title = t(`${baseI18nKey}.title`)
      const cleanTitle = title
        .replace(/<color.*?>/g, '')
        .replace(/<\/color>/g, '')
        .replace(/<br\s*\/?>/gi, '') // remove <br> and <br/> tags

      trackImpression({
        unitName: `${persona}_simulation${scenarioUnitName}`,
        unitVariation: `scenario_${t(`${baseI18nKey}.type`)}_${cleanTitle}`,
        unitLocation: 'tyce',
      })

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseI18nKey, scenarioUnitName, step])

    const onInnerNext = useCallback(() => {
      const title = t(`${baseI18nKey}.title`)
      const cleanTitle = title
        .replace(/<color.*?>/g, '')
        .replace(/<\/color>/g, '')
        .replace(/<br\s*\/?>/gi, '') // remove <br> and <br/> tags

      trackClick({
        unitName: `${persona}_simulation${scenarioUnitName}`,
        unitVariation: `scenario_${t(`${baseI18nKey}.type`)}_${cleanTitle}`,
        unitLocation: 'tyce',
        clickType: 'button',
        buttonText: isLast ? `lets_go` : `next`,
        buttonName: 'navigation_button',
      })

      onNext()
    }, [trackClick, onNext, t, baseI18nKey, persona, scenarioUnitName, isLast])

    return (
      <div key={`${baseI18nKey}.step_${step}`} ref={ref} className={`${className ? className : ''}`}>
        <UIIcon
          ref={backgroundIconRef}
          icon={innerBackground}
          className={`PopinCardStep__Background absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 w-full h-auto aspect-square ${backgroundColors[innerAccent]}`}
        />

        <div
          ref={contentRef}
          className='PopinCardStep__Content pointer-events-auto  relative flex flex-col items-center justify-center lg:mt-60'
          style={{
            // @ts-expect-error CSS Variables
            '--color-accent': accentColors[innerAccent],
          }}
        >
          <div className='relative w-full max-w-[80vw] md:max-w-[360px]'>
            <img
              className='Sticker z-1 absolute bottom-12 md:bottom-32 left-0 w-full aspect-300/160 object-contain'
              alt=''
              src={stickerUrl}
            />
          </div>

          <UITag variant='big' rotate='forced' rounded='light'>
            {t(`${baseI18nKey}.tag`)}
          </UITag>

          <h2
            id={`dialog-title-${id}`}
            className={`text-heading-h2 lg:text-[3.4rem] mx-58 md:mx-96 lg:mx-0 text-center mt-16 mb-8 ${textColors[innerAccent]}`}
          >
            <Trans components={components}>{`${baseI18nKey}.title`}</Trans>
          </h2>

          <p
            id={`dialog-description-${id}`}
            className={`mx-48 md:mx-64 mt-8 text-center antialiased font-body font-normal leading-relaxed text-18 md:text-20 ${textColors[innerAccent]}`}
          >
            <Trans components={components}>{`${baseI18nKey}.text`}</Trans>
          </p>

          <Button
            key={`${baseI18nKey}.step-${step}`}
            variant='tertiary'
            className={`mt-32 pointer-events-auto transition-opacity duration-300 ease-out-quad ${
              hideCta ? 'opacity-0 pointer-events-none' : ''
            }`}
            disabled={hideCta}
            appear
            appearDelay={0.7}
            onClick={onInnerNext}
          >
            {isLast ? t(ctaI18nKey) : t(`runner.next`)}
          </Button>
          <UIParticles
            className='absolute top-1/2 left-1/2 w-screen min-h-dvh md:h-screen -translate-x-1/2 -translate-y-1/2'
            max={6}
            exclude={{ colors: excludedColors[innerAccent] }}
          />
        </div>
      </div>
    )
  }
)

export default PopinCardStep
